import { useQuery } from "@tanstack/react-query";
import { Link2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../../shared/api/client";
import { Button, Card, CardBody, CardHeader, ErrorState } from "../../shared/ui";
import type { LinkView } from "../../shared/types/api";
import { WorkItemStatusBadge } from "./WorkItemTypeBadge";

/**
 * Dependencies (§85).
 *
 * <p>Grouped by how the relationship reads from <em>this</em> item, because that is the only
 * framing a reader here cares about. The server stores one row per relationship and words it for
 * whichever end asked, so "blocks" and "is blocked by" are the same fact seen from two places
 * rather than two records that could disagree.
 *
 * <p>Kept visually distinct from the children panel above it. §85 insists hierarchy and
 * dependency stay separate concepts, and two panels that looked alike would undo that in the one
 * place it matters — the screen where somebody is trying to tell them apart.
 */
export function WorkItemLinks({
  workItemId,
  canEdit,
  onAdd,
  onChanged,
}: {
  workItemId: string;
  canEdit: boolean;
  onAdd: () => void;
  onChanged: () => void;
}) {
  const navigate = useNavigate();

  const links = useQuery({
    queryKey: ["work-item-links", workItemId],
    queryFn: () => api.get<LinkView[]>(`/api/v1/work-items/${workItemId}/links`),
  });

  const remove = async (linkId: string) => {
    await api.del(`/api/v1/work-items/links/${linkId}`);
    void links.refetch();
    onChanged();
  };

  if (links.isError) {
    return <ErrorState error={links.error} onRetry={() => void links.refetch()} />;
  }

  const grouped = new Map<string, LinkView[]>();
  for (const link of links.data ?? []) {
    const existing = grouped.get(link.label);
    if (existing) {
      existing.push(link);
    } else {
      grouped.set(link.label, [link]);
    }
  }

  if (grouped.size === 0 && !canEdit) {
    return null;
  }

  return (
    <Card>
      <CardHeader
        title="Dependencies"
        subtitle="Work standing in the way, or related to this. Not the same as the hierarchy."
        action={
          canEdit && (
            <Button
              variant="ghost"
              size="sm"
              icon={<Link2 className="h-4 w-4" />}
              onClick={onAdd}
            >
              Link
            </Button>
          )
        }
      />
      <CardBody className={grouped.size === 0 ? undefined : "space-y-4"}>
        {grouped.size === 0 ? (
          <p className="text-sm text-ink-500">Nothing linked.</p>
        ) : (
          [...grouped.entries()].map(([label, group]) => (
            <section key={label}>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-400">
                {label}
              </p>
              <ul className="overflow-hidden rounded-lg border border-ink-200">
                {group.map((link) => (
                  <li
                    key={link.id}
                    className="flex items-center gap-3 border-b border-ink-100 px-3 py-2 last:border-0"
                  >
                    <button
                      type="button"
                      onClick={() => navigate(`/work-items/${link.otherKey}`)}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    >
                      <span className="font-mono text-xs text-brand-600">{link.otherKey}</span>
                      <span className="truncate text-sm text-ink-800">{link.otherTitle}</span>
                    </button>
                    <WorkItemStatusBadge status={link.otherStatus} />
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Remove this link"
                        className="text-ink-400 hover:text-red-600"
                        onClick={() => void remove(link.id)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </CardBody>
    </Card>
  );
}
